/**
 * PLP Scroll + Load-More Restoration
 *
 * Tracks "Load More" progress in a URL param (?plp_page=N) and scroll
 * position in history.state.  Both survive the forced location.reload()
 * that micasa-theme.js triggers on every back navigation, so no fragile
 * pageshow-flag dance is required.
 *
 * Flow:
 *  1. User clicks Load More → URL becomes ?plp_page=2, then ?plp_page=3 …
 *  2. User clicks a product → scroll Y saved to history.state.plpScrollY
 *  3. User presses Back → browser restores the PLP URL (?plp_page=3 intact)
 *  4. micasa-theme.js forces location.reload() → URL is preserved
 *  5. On the reloaded page ?plp_page=3 is in the URL → script silently
 *     fetches pages 2 and 3, appends products, then restores scroll
 *
 * Extra benefit: sharing or bookmarking a ?plp_page URL shows the same
 * product set as the original browse session.
 */
(function () {
  'use strict';

  var PLP_PAGE_PARAM = 'plp_page';
  var RESTORE_MASK_CLASS = 'plp-restoring-back-nav';
  var restoreMaskTimeoutId = null;

  function ensureRestoreMaskStyle() {
    if (document.getElementById('plp-restore-mask-style')) return;

    var styleEl = document.createElement('style');
    styleEl.id = 'plp-restore-mask-style';
    styleEl.textContent =
      'html.' + RESTORE_MASK_CLASS + ' body { opacity: 0 !important; }' +
      'html.' + RESTORE_MASK_CLASS + ' body * { transition: none !important; }';
    document.head.appendChild(styleEl);
  }

  function startRestoreMask() {
    ensureRestoreMaskStyle();
    document.documentElement.classList.add(RESTORE_MASK_CLASS);
    if (restoreMaskTimeoutId) {
      clearTimeout(restoreMaskTimeoutId);
    }

    // Safety net to avoid a stuck hidden page.
    restoreMaskTimeoutId = setTimeout(function () {
      endRestoreMask('timeout');
    }, 4000);
  }

  function endRestoreMask(reason) {
    if (restoreMaskTimeoutId) {
      clearTimeout(restoreMaskTimeoutId);
      restoreMaskTimeoutId = null;
    }
    if (document.documentElement.classList.contains(RESTORE_MASK_CLASS)) {
      document.documentElement.classList.remove(RESTORE_MASK_CLASS);
    }
  }

  var productGridContainer = document.getElementById('ProductGridContainer');
  if (!productGridContainer) {
    return;
  }

  // Disable browser-native scroll restoration – we control it ourselves.
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  // -------------------------------------------------------------------------
  // URL-param helpers (all use replaceState so no extra history entry is added)
  // -------------------------------------------------------------------------
  function getPageParam() {
    return new URLSearchParams(location.search).get(PLP_PAGE_PARAM);
  }

  function setPageParam(value) {
    var params = new URLSearchParams(location.search);
    params.set(PLP_PAGE_PARAM, value);
    history.replaceState(history.state, '', location.pathname + '?' + params.toString());
  }

  function removePageParam() {
    var params = new URLSearchParams(location.search);
    params.delete(PLP_PAGE_PARAM);
    var qs = params.toString();
    history.replaceState(history.state, '', location.pathname + (qs ? '?' + qs : ''));
  }

  // -------------------------------------------------------------------------
  // Track Load More clicks → update ?plp_page in the URL
  // Capture phase fires before LoadMoreButton's own handler, giving us the
  // action URL (e.g. ?page=2) before the button is replaced with ?page=3.
  // -------------------------------------------------------------------------
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest('[is="load-more-button"]');
      if (!btn || btn.classList.contains('btn--loading')) return;
      var action = btn.getAttribute('action');
      if (!action) return;
      try {
        var pageNum = parseInt(new URL(action, location.origin).searchParams.get('page'), 10);
        if (!isNaN(pageNum) && pageNum >= 2) setPageParam(pageNum);
      } catch (_) {}
    },
    true // capture
  );

  // When filters / sorting change the grid is fully re-rendered by facets.js –
  // any loaded extra pages are gone, so remove the param and reset the URL.
  document.addEventListener('collection:rerendered', function () {
    removePageParam();
  });

  // -------------------------------------------------------------------------
  // Save scroll position into history.state when the user clicks a product.
  // history.state survives location.reload() so it's available after the
  // forced cart-refresh reload that micasa-theme.js performs.
  // -------------------------------------------------------------------------
  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[href]');
    if (!link || !productGridContainer.contains(link)) return;
    var href = link.getAttribute('href');
    if (!href || href.indexOf('/products/') === -1) return;
    try {
      history.replaceState(
        Object.assign({}, history.state || {}, {
          plpScrollY: window.scrollY,
          plpRestorePending: true,
        }),
        '',
        location.href
      );
    } catch (_) {}
  });

  // -------------------------------------------------------------------------
  // Restore on page load – triggered purely by the presence of ?plp_page=N
  // -------------------------------------------------------------------------
  var maxPage = parseInt(getPageParam(), 10);
  var targetScrollY = (history.state && history.state.plpScrollY) || 0;
  var hasPendingStateRestore =
    !!(history.state && history.state.plpRestorePending) && targetScrollY > 0;
  var hasLoadMoreRestore = !isNaN(maxPage) && maxPage >= 2;

  if (!hasLoadMoreRestore && !hasPendingStateRestore) {
    return; // nothing to restore
  }

  // Hide the page briefly so users don't see top-of-page flash before restore.
  startRestoreMask();

  if (hasLoadMoreRestore) {
    restorePages(maxPage, targetScrollY);
  } else {
    restoreScroll(targetScrollY);
  }

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  function getSectionId() {
    var section = productGridContainer.closest('.shopify-section');
    if (section) return section.id.replace('shopify-section-', '');
    var fallback = document.querySelector('.shopify-section[id*="collection"]');
    return fallback ? fallback.id.replace('shopify-section-', '') : null;
  }

  function getLoadMoreNextPage() {
    var btn = productGridContainer.querySelector('[is="load-more-button"]');
    if (!btn) return null;

    var action = btn.getAttribute('action');
    if (!action) return null;

    try {
      var pageNum = parseInt(new URL(action, location.origin).searchParams.get('page'), 10);
      return isNaN(pageNum) ? null : pageNum;
    } catch (_) {
      return null;
    }
  }

  function restorePages(maxPage, targetScrollY) {
    var sectionId = getSectionId();
    var productList = productGridContainer.querySelector('.products-list');
    var nextPageFromButton = getLoadMoreNextPage();
    var expectedNextPage = maxPage + 1;
    var skipFetchRestore = nextPageFromButton !== null && nextPageFromButton >= expectedNextPage;

    if (!productList || !sectionId) {
      restoreScroll(targetScrollY);
      return;
    }

    if (skipFetchRestore) {
      restoreScroll(targetScrollY);
      return;
    }

    // Hide the load-more button during silent restoration so the user doesn't
    // see it in a broken mid-state.
    var existingBtn = productGridContainer.querySelector('[is="load-more-button"]');
    if (existingBtn) existingBtn.style.visibility = 'hidden';

    // Build base params from the current URL minus plp_page and page
    var baseParams = new URLSearchParams(location.search);
    baseParams.delete(PLP_PAGE_PARAM);
    baseParams.delete('page');

    var chain = Promise.resolve();

    for (var p = 2; p <= maxPage; p++) {
      (function (pageNum) {
        chain = chain.then(function () {
          var fetchParams = new URLSearchParams(baseParams.toString());
          fetchParams.set('page', pageNum);
          fetchParams.set('section_id', sectionId);
          var fetchUrl = location.pathname + '?' + fetchParams.toString();

          return fetch(fetchUrl)
            .then(function (response) {
              if (!response.ok) return;
              return response.text();
            })
            .then(function (html) {
              if (!html) return;
              var parsed = new DOMParser().parseFromString(html, 'text/html');

              var sourceList = parsed.querySelector('.products-list');
              if (sourceList) {
                sourceList.querySelectorAll('.f-column').forEach(function (item) {
                  productList.appendChild(item);
                });
              }

              // Keep the pagination button pointing at the correct next page.
              var paginationEl = productGridContainer.querySelector('.pagination');
              var sourcePagination = parsed.querySelector('.pagination');
              if (paginationEl) {
                if (sourcePagination) {
                  paginationEl.innerHTML = sourcePagination.innerHTML;
                } else {
                  paginationEl.remove();
                }
              }
            })
            .catch(function () {
              // Network error – continue with remaining pages
            });
        });
      })(p);
    }

    chain.then(function () {
      restoreScroll(targetScrollY);
    });
  }

  function restoreScroll(scrollY) {
    // Two rAF frames let the browser finish layout before we apply the offset.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var previousScrollBehavior = document.documentElement.style.scrollBehavior;
        document.documentElement.style.scrollBehavior = 'auto';
        window.scrollTo({ top: scrollY, behavior: 'auto' });
        document.documentElement.style.scrollBehavior = previousScrollBehavior;

        try {
          history.replaceState(
            Object.assign({}, history.state || {}, { plpRestorePending: false }),
            '',
            location.href
          );
        } catch (_) {}

        // Reveal only after scroll is applied.
        requestAnimationFrame(function () {
          endRestoreMask('scroll-restored');
        });
      });
    });
  }
})();
